import { Carousel } from '@faststore/ui'
import { memo, useEffect, useRef, useState } from 'react'
import { PauseIcon } from 'src/components/icons/PauseIcon'
import { PlayIcon } from 'src/components/icons/PlayIcon'

import './CarouselInformative.scss'
import Slide from './Slide/Slide'
import type {
  CarouselInformativeItemsProps,
  CarouselInformativeProps,
} from './carousel-informative'
import { BASE_CLASS_NAME } from './utils'

interface CarouselInformativeSlidesProps {
  data: CarouselInformativeProps
}

const CarouselInformativeSlides = ({
  data,
}: CarouselInformativeSlidesProps) => {
  const carouselRef = useRef<HTMLElement | null>(null)
  const arrowRef = useRef<HTMLElement | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [isAutoPlay, setIsAutoPlay] = useState<boolean>(false)

  const infiniteMode = data?.carouselItems?.length > 1
  const controls = data?.carouselItems?.length > 1 ? 'complete' : undefined

  const classSlide = `${BASE_CLASS_NAME}__slide`

  const clickHandler = () => {
    if (isAutoPlay) {
      clearInterval(timerRef.current as NodeJS.Timer)
    } else {
      timerRef.current = setInterval(() => {
        arrowRef?.current?.click()
      }, 5000)
    }

    setIsAutoPlay((prev) => !prev)
  }

  useEffect(() => {
    let nextArrow

    if (carouselRef.current) {
      nextArrow = carouselRef.current.querySelector(
        "button[data-arrow='right']"
      ) as HTMLElement
    }

    if (data.autoplay.isEnabled && nextArrow && typeof window !== 'undefined') {
      setIsAutoPlay(true)
      arrowRef.current = nextArrow
      timerRef.current = setInterval(() => {
        arrowRef?.current?.click()
      }, data.autoplay.duration ?? 4000)
    }

    return () => clearInterval(timerRef.current as NodeJS.Timer)
  }, [data.autoplay])

  if (!data?.carouselItems?.length) {
    return <></>
  }

  return (
    <section
      className={`${BASE_CLASS_NAME} ${
        data?.carouselItems?.length <= 1 && `${BASE_CLASS_NAME}--hiddenControls`
      }`}
      style={{
        marginTop: `${data?.marginTop}`,
        marginBottom: `${data?.marginBottom}`,
        position: 'relative',
      }}
      ref={carouselRef}
    >
      <Carousel
        infiniteMode={infiniteMode}
        controls={controls}
        transition={{
          duration: 400,
          property: 'transform',
        }}
      >
        {data?.carouselItems?.map(
          (slide: CarouselInformativeItemsProps, index: number) => {
            return (
              <Slide
                key={index}
                content={slide.content}
                background={slide.background}
                classSlide={classSlide}
                index={index}
              />
            )
          }
        )}
      </Carousel>
      {data?.carouselItems?.length > 1 && (
        <div
          role="presentation"
          onClick={clickHandler}
          id="autoplay-button"
          className="autoplay-button__container"
        >
          {isAutoPlay ? (
            <PauseIcon width={16} height={16} color="#000" />
          ) : (
            <PlayIcon width={16} height={16} color="#000" />
          )}
        </div>
      )}
    </section>
  )
}

export default memo(CarouselInformativeSlides)
